import { KUUKAUDET } from './constants';
import type { Lead, LeadContact, Settings, Template } from '../types';

export const todayStr = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export const fmtDate = (s: string | null | undefined): string =>
  s ? s.slice(0, 10).split('-').reverse().join('.') : '';

/** Viimeisin kontakti kuluneena aikana. 2–6 päivää näytetään päivän tarkkuudella. */
export function kontaktiKulunut(iso: string, tanaan: string): string {
  const paiva = iso.slice(0, 10);
  const [vy, kk, pv] = paiva.split('-').map(Number);
  const [ty, tk, tp] = tanaan.split('-').map(Number);
  if (!vy || !ty) return fmtDate(iso);
  const d = Math.round((Date.UTC(ty, tk - 1, tp) - Date.UTC(vy, kk - 1, pv)) / 86400000);
  if (d <= 0) return 'Tänään';
  if (d === 1) return 'Eilen';
  if (d <= 6) return `${d} päivää sitten`;
  if (d < 14) return 'Yli viikko sitten';
  if (d < 30) return 'Yli kaksi viikkoa sitten';
  return 'Yli kuukausi sitten';
}

export const kuukausiNimi = (m: string): string => {
  const [v, k] = String(m).split('-');
  return `${KUUKAUDET[Number(k) - 1] || m} ${v}`;
};

export const raha = (n: number, valuutta: 'EUR' | 'USD'): string =>
  (valuutta === 'USD' ? '$' : '')
  + n.toLocaleString('fi-FI', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  + (valuutta === 'USD' ? '' : ' €');

export const kokoTeksti = (tavua: number): string =>
  tavua >= 1e6
    ? `${(tavua / 1e6).toFixed(1).replace('.', ',')} Mt`
    : `${Math.max(1, Math.round(tavua / 1000))} kt`;

const firstName = (name: string | null | undefined): string =>
  (name || '').trim().split(/\s+/)[0] || '';

export const signature = (s: Settings): string =>
  [s.myyja, s.myyja_titteli, s.oma_yritys, s.myyja_puhelin, s.myyja_email]
    .filter((x): x is string => !!x && !!x.trim())
    .join('\n');

/** Korvaa liidin yhteystiedot valitulla henkilöllä (viestipohjaa varten). */
export function leadWithContact(lead: Lead, contact?: LeadContact | null): Lead {
  if (!contact) return lead;
  return {
    ...lead,
    contact_name: contact.name || null,
    title: contact.title || null,
    email: contact.email || null,
    phone: contact.phone || null,
  };
}

/** Täyttää viestipohjan paikkamerkit liidin ja asetusten tiedoilla. */
export function fill(text: string, lead: Lead, s: Settings): string {
  const map: Record<string, string> = {
    yritys: lead.company || '',
    nimi: lead.contact_name || '',
    etunimi: firstName(lead.contact_name),
    kaupunki: lead.city || '',
    toimiala: lead.industry || '',
    myyja: s.myyja || '',
    myyja_titteli: s.myyja_titteli || '',
    myyja_puhelin: s.myyja_puhelin || '',
    myyja_email: s.myyja_email || '',
    oma_yritys: s.oma_yritys || '',
    allekirjoitus: signature(s),
  };
  return text
    .replace(/\{\{(\w+)\}\}/g, (m, k: string) => (k in map ? map[k] : m))
    .replace(/\s*\(\s*\)/g, '')        // tyhjä (kaupunki) pois
    .replace(/[ \t]+([?.!,])/g, '$1')  // "Hei ," -> "Hei,"
    .replace(/ {2,}/g, ' ');
}

/** Toimialan mukainen viestipohja, muuten ensimmäinen. */
export const pickTemplate = (lead: Lead, templates: Template[]): Template | undefined =>
  templates.find(t => t.industry && t.industry === lead.industry) || templates[0];

/** Pilkkueroteltu id-lista taulukoksi (GROUP_CONCAT palvelimelta). */
export const idLista = (s: string | null | undefined): number[] =>
  String(s || '').split(',').filter(Boolean).map(Number);

/**
 * Liikevaihto lyhyessä muodossa: suuret luvut miljoonina, pienet tuhansina.
 * Tarkka euro ei kerro myyjälle mitään enempää kuin ”2,4 M€”, ja pitkä
 * numerosarja hukuttaisi tulostaulukon kapeat sarakkeet.
 */
export const liikevaihto = (eur: number | null | undefined): string => {
  if (!eur || eur <= 0) return '—';
  if (eur >= 1e6) return `${(eur / 1e6).toFixed(1).replace('.', ',')} M€`;
  return `${Math.round(eur / 1000)} t€`;
};
